import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dates";
import {
  Rocket, Plus, X, Pencil, Trash2, CircleDot, CheckCircle2, Clock, AlertTriangle, Ban, Target,
  DollarSign, LayoutGrid, GanttChartSquare, List, Users2, Activity, MessageSquare, Send, Flag, Link2, Send as SendIcon,
  Kanban as KanbanIcon, Cloud, RefreshCw, ExternalLink,
} from "lucide-react";

import DOMPurify from "dompurify";

/**
 * Lightweight markdown renderer for project descriptions. Supports the subset
 * used by RiskEye's assessment-to-project auto-fill (headings, bold, bullets,
 * links, line breaks) and pipes the output through DOMPurify.
 */
function ProjectDescription({ markdown }) {
  const html = useMemo(() => {
    if (!markdown) return "";
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let body = esc(markdown);
    // Headings
    body = body.replace(/^###\s+(.+)$/gm, '<h4 class="font-display font-semibold text-slate-900 text-[14px] mt-4 mb-1.5">$1</h4>');
    body = body.replace(/^##\s+(.+)$/gm, '<h3 class="font-display font-semibold text-slate-900 text-[15px] mt-5 mb-2 pb-1 border-b border-slate-100">$1</h3>');
    body = body.replace(/^#\s+(.+)$/gm,  '<h2 class="font-display font-bold text-slate-900 text-[17px] mt-5 mb-2">$1</h2>');
    // Bold + italic + code
    body = body.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
    body = body.replace(/`([^`]+)`/g, '<code class="bg-slate-100 border border-slate-200 rounded px-1 py-0.5 font-mono text-[11.5px] text-slate-800">$1</code>');
    body = body.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="text-cyan-700 hover:text-cyan-900 underline" href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    // Bullet lists — one-liner treatment
    body = body.replace(/(^|\n)- (.+)/g, '$1<li class="ml-5 list-disc marker:text-slate-400 leading-relaxed">$2</li>');
    // Paragraph breaks
    body = body.split(/\n{2,}/).map((chunk) => {
      if (chunk.startsWith("<h") || chunk.startsWith("<li")) return chunk.replace(/\n/g, " ");
      return `<p class="text-[13.5px] text-slate-700 leading-relaxed my-2">${chunk.replace(/\n/g, "<br/>")}</p>`;
    }).join("");
    return DOMPurify.sanitize(body, {
      ALLOWED_TAGS: ["h2", "h3", "h4", "p", "strong", "em", "code", "a", "li", "ul", "br", "span"],
      ALLOWED_ATTR: ["class", "href", "target", "rel"],
    });
  }, [markdown]);
  // XSS-safe: `html` is produced by DOMPurify.sanitize with a strict
  // ALLOWED_TAGS / ALLOWED_ATTR allowlist above. Scanner false-positive on
  // the `dangerouslySetInnerHTML` keyword alone.
  return <div className="mt-2 text-[13.5px] text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} data-testid="project-description-md" />;
}

const STATUSES = [
  { key: "Planning",   label: "Planning",    icon: CircleDot,     color: "text-slate-700",    bg: "bg-slate-50",    border: "border-slate-200",   bar: "bg-slate-400" },
  { key: "InProgress", label: "In progress", icon: Clock,         color: "text-cyan-700",     bg: "bg-cyan-50",     border: "border-cyan-200",    bar: "bg-cyan-500" },
  { key: "AtRisk",     label: "At risk",     icon: AlertTriangle, color: "text-amber-800",    bg: "bg-amber-50",    border: "border-amber-200",   bar: "bg-amber-500" },
  { key: "OnHold",     label: "On hold",     icon: Ban,           color: "text-slate-600",    bg: "bg-slate-100",   border: "border-slate-300",   bar: "bg-slate-500" },
  { key: "Complete",   label: "Complete",    icon: CheckCircle2,  color: "text-emerald-700",  bg: "bg-emerald-50",  border: "border-emerald-200", bar: "bg-emerald-500" },
  { key: "Cancelled",  label: "Cancelled",   icon: X,             color: "text-rose-700",     bg: "bg-rose-50",     border: "border-rose-200",    bar: "bg-rose-500" },
];
const HEALTH = [
  { key: "Green", label: "On track", color: "bg-emerald-500", ring: "ring-emerald-300",    softBg: "bg-emerald-50",    softBorder: "border-emerald-200" },
  { key: "Amber", label: "Watch",    color: "bg-amber-500",   ring: "ring-amber-300",      softBg: "bg-amber-50",      softBorder: "border-amber-200" },
  { key: "Red",   label: "Blocked",  color: "bg-rose-500",    ring: "ring-rose-300",       softBg: "bg-rose-50",       softBorder: "border-rose-200" },
];
const TASK_STATUSES = ["Todo", "InProgress", "Blocked", "Done"];
const statusMeta = (s) => STATUSES.find((x) => x.key === s) || STATUSES[0];
const healthMeta = (h) => HEALTH.find((x) => x.key === h) || HEALTH[0];

// Owner-avatar palette derived from the owner name so the same person always
// gets the same colour — familiar in Kanban / assignment views.
const AVATAR_PALETTE = [
  "from-cyan-500 to-blue-600", "from-emerald-500 to-teal-600", "from-amber-500 to-orange-600",
  "from-rose-500 to-pink-600", "from-violet-500 to-fuchsia-600", "from-indigo-500 to-blue-700",
  "from-lime-500 to-emerald-600", "from-orange-500 to-rose-600",
];
function _avatarBg(name) {
  const s = (name || "").toString();
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}
function Avatar({ name, size = 24, className = "", title }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const dim = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  return (
    <span
      title={title || name}
      style={dim}
      className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br ${_avatarBg(name)} text-white font-semibold ring-2 ring-white shrink-0 ${className}`}
    >
      {initials}
    </span>
  );
}

function M365Chip({ source, className = "" }) {
  if (!source || source.type !== "m365_assessment") return null;
  return (
    <span
      title={`Auto-created from M365 CIS ${source.control_id} · ${source.level}`}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5 ${className}`}
      data-testid="project-m365-chip"
    >
      <Cloud className="h-3 w-3" /> CIS {source.control_id}
    </span>
  );
}

export default function Projects() {
  const { can } = useAuth();
  const canWrite = can("risk.write");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  // Persist per-user view + kanban grouping so their preference sticks.
  const [view, setView] = useState(() => localStorage.getItem("projects.view") || "kanban");
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("projects.groupBy") || "status");
  useEffect(() => { localStorage.setItem("projects.view", view); }, [view]);
  useEffect(() => { localStorage.setItem("projects.groupBy", groupBy); }, [groupBy]);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/projects"); setProjects(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => statusFilter === "all" ? projects : projects.filter((p) => p.status === statusFilter), [projects, statusFilter]);

  const remove = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Drag-drop: patch a project's status or health. Optimistic-update the
  // local list so cards visibly move without a network round-trip flash.
  const moveProject = async (projectId, field, value) => {
    setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, [field]: value } : p)));
    try {
      await api.patch(`/projects/${projectId}`, { [field]: value });
      toast.success(field === "status" ? "Project moved" : "Health updated");
    } catch (e) {
      toast.error(apiError(e));
      load(); // rollback
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="projects-page">
      <PageHeader kicker="Projects" title="Projects"
        description="Track programmes end-to-end. RAG health, budget, milestones, dependencies and linked risks. Switch between Cards, Gantt and List views."
        actions={canWrite && (
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setDialog({ open: true, editing: null })} data-testid="project-new">
            <Plus className="h-4 w-4 mr-1.5" /> New Project
          </Button>
        )} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="project-filters">
          <FilterBtn active={statusFilter === "all"} label="All" count={projects.length} onClick={() => setStatusFilter("all")} testid="project-filter-all" />
          {STATUSES.map((s) => (
            <FilterBtn key={s.key} active={statusFilter === s.key} label={s.label} count={projects.filter((p) => p.status === s.key).length}
              onClick={() => setStatusFilter(s.key)} icon={s.icon} testid={`project-filter-${s.key.toLowerCase()}`} />
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="project-view-toggle">
          <ViewBtn active={view === "kanban"} onClick={() => setView("kanban")} icon={KanbanIcon} label="Kanban" testid="project-view-kanban" />
          <ViewBtn active={view === "cards"} onClick={() => setView("cards")} icon={LayoutGrid} label="Cards" testid="project-view-cards" />
          <ViewBtn active={view === "gantt"} onClick={() => setView("gantt")} icon={GanttChartSquare} label="Gantt" testid="project-view-gantt" />
          <ViewBtn active={view === "list"} onClick={() => setView("list")} icon={List} label="List" testid="project-view-list" />
        </div>
      </div>

      {loading ? <LoadingSkeleton /> : filtered.length === 0 ? (
        <EmptyState icon={Rocket} title={projects.length === 0 ? "No projects yet" : "No projects match your filter"}
          description={projects.length === 0 ? "Kick off delivery by capturing your first initiative - link it to the risks it treats." : "Try switching to a different status."} />
      ) : view === "kanban" ? (
        <KanbanView projects={filtered} onOpen={setDetail} onMove={canWrite ? moveProject : null}
          groupBy={groupBy} setGroupBy={setGroupBy} />
      ) : view === "cards" ? (
        <ProjectCards projects={filtered} onOpen={setDetail} />
      ) : view === "gantt" ? (
        <GanttView projects={filtered} onOpen={setDetail} />
      ) : (
        <ListView projects={filtered} onOpen={setDetail} />
      )}

      {dialog.open && canWrite && (
        <ProjectDialog existing={dialog.editing}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => { setDialog({ open: false, editing: null }); load(); }} />
      )}
      {detail && (
        <ProjectDrawer projectId={detail} canWrite={canWrite}
          onClose={() => setDetail(null)}
          onEdit={(p) => { setDetail(null); setDialog({ open: true, editing: p }); }}
          onDelete={async (id) => { await remove(id); setDetail(null); }}
          onChanged={load} />
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, label, count, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium border inline-flex items-center gap-1.5 ${active ? "bg-[#0A1329] text-white border-[#0A1329]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label} <span className="text-[11px] font-mono opacity-70">{count}</span>
    </button>
  );
}
function ViewBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium inline-flex items-center gap-1.5 ${active ? "bg-[#0A1329] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function ProjectCards({ projects, onOpen }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="project-list">
      {projects.map((p) => {
        const sm = statusMeta(p.status);
        const hm = healthMeta(p.health);
        const pct = p.progress || 0;
        return (
          <Card key={p.id} onClick={() => onOpen(p.id)}
            className="p-5 bg-white border border-slate-200 hover:shadow-md hover:border-slate-300 cursor-pointer transition-all"
            data-testid={`project-card-${p.id.slice(0, 8)}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${sm.color} ${sm.bg} border ${sm.border} rounded px-1.5 py-0.5`}>
                  <sm.icon className="h-3 w-3" /> {sm.label}
                </span>
                <M365Chip source={p.source} />
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-600" title={hm.label}>
                <span className={`h-2 w-2 rounded-full ${hm.color}`} /> {hm.label}
              </span>
            </div>
            <h3 className="font-display font-semibold text-slate-900 text-[16px] leading-snug">{p.name}</h3>
            {p.description && <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed line-clamp-2">{p.description}</p>}
            <div className="mt-4">
              <div className="flex items-center justify-between text-[11px] font-mono text-slate-500 mb-1">
                <span>{p.task_done || 0}/{p.task_total || 0} tasks</span>
                <span className="tabular-nums text-slate-800 font-semibold">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 font-mono">
              <span className="flex items-center gap-1.5 min-w-0">
                <Avatar name={p.owner_name || "Unassigned"} size={22} />
                <span className="truncate">{p.owner_name || "Unassigned"}</span>
              </span>
              <div className="flex items-center gap-3">
                {p.target_date && <span className="flex items-center gap-1"><Target className="h-3 w-3" />{formatDate(p.target_date)}</span>}
                {p.budget != null && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{Number(p.budget).toLocaleString()}</span>}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------- Kanban ----------------
function KanbanView({ projects, onOpen, onMove, groupBy, setGroupBy }) {
  const groups = groupBy === "health" ? HEALTH.map((h) => ({ key: h.key, label: h.label, meta: h }))
                                       : STATUSES.map((s) => ({ key: s.key, label: s.label, meta: s }));
  const field = groupBy === "health" ? "health" : "status";
  const bucketed = useMemo(() => {
    const b = {}; groups.forEach((g) => { b[g.key] = []; });
    projects.forEach((p) => { const k = p[field] || groups[0].key; (b[k] || (b[k] = [])).push(p); });
    return b;
  }, [projects, groups, field]);

  const [dragging, setDragging] = useState(null); // { id, from }
  const [hoverKey, setHoverKey] = useState(null);

  const onDragStart = (p) => (e) => {
    if (!onMove) return e.preventDefault();
    setDragging({ id: p.id, from: p[field] });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", p.id); } catch {}
  };
  const onDragOver = (key) => (e) => {
    if (!onMove || !dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoverKey !== key) setHoverKey(key);
  };
  const onDrop = (key) => (e) => {
    e.preventDefault();
    setHoverKey(null);
    if (!onMove || !dragging) return;
    if (dragging.from !== key) onMove(dragging.id, field, key);
    setDragging(null);
  };
  const onDragEnd = () => { setDragging(null); setHoverKey(null); };

  return (
    <div data-testid="project-kanban">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {onMove ? "Drag cards between lanes to update" : "Read-only — ask an admin for edit access"}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="kanban-groupby-toggle">
          <button onClick={() => setGroupBy("status")} data-testid="kanban-groupby-status"
            className={`px-2.5 py-1 rounded-md text-[11.5px] font-medium ${groupBy === "status" ? "bg-[#0A1329] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            by Status
          </button>
          <button onClick={() => setGroupBy("health")} data-testid="kanban-groupby-rag"
            className={`px-2.5 py-1 rounded-md text-[11.5px] font-medium ${groupBy === "health" ? "bg-[#0A1329] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            by RAG
          </button>
        </div>
      </div>

      <div className={`grid gap-3 ${groupBy === "health" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"}`}>
        {groups.map((g) => {
          const isHealth = groupBy === "health";
          const items = bucketed[g.key] || [];
          const hovering = hoverKey === g.key;
          return (
            <div
              key={g.key}
              onDragOver={onDragOver(g.key)}
              onDragLeave={() => hovering && setHoverKey(null)}
              onDrop={onDrop(g.key)}
              className={`rounded-xl border p-3 min-h-[280px] flex flex-col transition-colors ${
                hovering ? "border-cyan-400 bg-cyan-50/60 ring-2 ring-cyan-200" : "border-slate-200 bg-slate-50/70"
              }`}
              data-testid={`kanban-lane-${g.key.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2 px-0.5">
                <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-widest text-slate-600">
                  {isHealth ? (
                    <span className={`h-2 w-2 rounded-full ${g.meta.color}`} />
                  ) : (
                    <g.meta.icon className={`h-3.5 w-3.5 ${g.meta.color}`} />
                  )}
                  {g.label}
                </div>
                <span className="text-[10.5px] font-mono text-slate-500 tabular-nums">{items.length}</span>
              </div>
              <div className="space-y-2 flex-1">
                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-[11px] font-mono text-slate-400">
                    {onMove ? "Drop here" : "Empty"}
                  </div>
                ) : items.map((p) => <KanbanCard key={p.id} project={p} onOpen={onOpen}
                                                 draggable={!!onMove} onDragStart={onDragStart(p)} onDragEnd={onDragEnd}
                                                 isDragging={dragging?.id === p.id} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function StatChip({ label, value }) {
  return (
    <div className="rounded-lg bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-2 text-center">
      <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-200">{label}</div>
      <div className="font-display font-bold text-[18px] text-white tabular-nums leading-tight mt-0.5">{value}</div>
    </div>
  );
}



function KanbanCard({ project: p, onOpen, draggable, onDragStart, onDragEnd, isDragging }) {
  const sm = statusMeta(p.status);
  const hm = healthMeta(p.health);
  const pct = p.progress || 0;
  const isM365 = p.source?.type === "m365_assessment";
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(p.id)}
      data-testid={`kanban-card-${p.id.slice(0, 8)}`}
      className={`group rounded-lg border bg-white p-3 shadow-sm hover:shadow-md hover:border-slate-300 transition-all cursor-pointer ${
        isDragging ? "opacity-40 ring-2 ring-cyan-400 rotate-[0.5deg]" : "border-slate-200"
      } ${isM365 ? "border-l-[3px] border-l-cyan-500" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-slate-900 leading-snug line-clamp-2">{p.name}</div>
          {isM365 && (
            <div className="mt-1 text-[10px] font-mono text-cyan-800 truncate">
              <Cloud className="h-2.5 w-2.5 inline mr-1" /> CIS {p.source.control_id} · {p.source.level}
            </div>
          )}
        </div>
        <span title={hm.label} className={`h-2.5 w-2.5 rounded-full ${hm.color} ring-2 ring-white shrink-0 mt-1`} />
      </div>

      <div className="mt-2.5">
        <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 mb-1">
          <span>{p.task_done || 0}/{p.task_total || 0} tasks</span>
          <span className="tabular-nums text-slate-700 font-semibold">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${sm.bar} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2 min-w-0">
        <Avatar name={p.owner_name || "Unassigned"} size={22} title={p.owner_name || "Unassigned"} />
        <div className="flex items-center gap-2 text-[10.5px] font-mono text-slate-500 min-w-0 overflow-hidden">
          {p.target_date && (
            <span className="flex items-center gap-0.5 shrink-0" title={`Target ${formatDate(p.target_date)}`}>
              <Target className="h-3 w-3" /> {formatDate(p.target_date)}
            </span>
          )}
          {p.budget != null && (
            <span className="flex items-center gap-0.5 min-w-0 truncate" title={`Budget $${Number(p.budget).toLocaleString()}`}>
              <DollarSign className="h-3 w-3 shrink-0" /> <span className="truncate">{Number(p.budget).toLocaleString()}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function GanttView({ projects, onOpen }) {
  const withDates = projects.filter((p) => p.start_date && p.target_date);
  if (!withDates.length) return <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-[13px] text-slate-500">No projects have start + target dates set. Edit a project to set them, then switch back to the Gantt view.</div>;
  const min = Math.min(...withDates.map((p) => new Date(p.start_date).getTime()));
  const max = Math.max(...withDates.map((p) => new Date(p.target_date).getTime()));
  const span = Math.max(max - min, 86400000);
  const months = [];
  const d = new Date(min); d.setDate(1);
  while (d.getTime() <= max) {
    months.push(new Date(d));
    d.setMonth(d.getMonth() + 1);
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="project-gantt">
      <div className="min-w-[820px]">
        <div className="grid grid-cols-[220px_1fr] border-b border-slate-100 bg-slate-50/60">
          <div className="px-4 py-3 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Project</div>
          <div className="relative h-9">
            <div className="absolute inset-0 flex">
              {months.map((m) => (
                <div key={m.toISOString()} className="flex-1 border-l border-slate-100 text-[10px] font-mono uppercase tracking-widest text-slate-400 pt-2 px-2">
                  {m.toLocaleString(undefined, { month: "short", year: "2-digit" })}
                </div>
              ))}
            </div>
          </div>
        </div>
        {withDates.map((p) => {
          const s = new Date(p.start_date).getTime();
          const e = new Date(p.target_date).getTime();
          const left = ((s - min) / span) * 100;
          const width = Math.max(((e - s) / span) * 100, 2);
          const sm = statusMeta(p.status);
          const hm = HEALTH.find((h) => h.key === p.health) || HEALTH[0];
          return (
            <div key={p.id} className="grid grid-cols-[220px_1fr] border-b border-slate-100 items-center hover:bg-slate-50/40 cursor-pointer" onClick={() => onOpen(p.id)} data-testid={`gantt-row-${p.id.slice(0, 8)}`}>
              <div className="px-4 py-3 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">{p.name}</div>
                <div className="text-[10.5px] font-mono text-slate-500 flex items-center gap-2 mt-0.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${hm.color}`} /> {sm.label}
                </div>
              </div>
              <div className="relative h-14">
                <div className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-md ${sm.bar} opacity-80 border border-white/50 shadow-sm flex items-center px-2 text-[10.5px] font-mono text-white`}
                  style={{ left: `${left}%`, width: `${width}%`, minWidth: "40px" }} title={`${formatDate(p.start_date)} → ${formatDate(p.target_date)}`}>
                  <span className="truncate">{p.progress || 0}%</span>
                </div>
                <div className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-md bg-white/40`}
                  style={{ left: `${left}%`, width: `${(width * (p.progress || 0)) / 100}%`, mixBlendMode: "overlay" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ projects, onOpen }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="project-list-table">
      <table className="w-full text-[13px]">
        <thead className="bg-slate-900 text-white text-[10.5px] font-mono uppercase tracking-widest">
          <tr>
            <th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Health</th><th className="text-left px-4 py-3">Owner</th>
            <th className="text-left px-4 py-3">Progress</th><th className="text-left px-4 py-3">Target</th>
            <th className="text-left px-4 py-3">Budget</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => {
            const sm = statusMeta(p.status);
            const hm = HEALTH.find((h) => h.key === p.health) || HEALTH[0];
            return (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => onOpen(p.id)} data-testid={`project-row-${p.id.slice(0, 8)}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-[11px] font-mono ${sm.color}`}><sm.icon className="h-3 w-3" />{sm.label}</span></td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-600"><span className={`h-2 w-2 rounded-full ${hm.color}`} /> {hm.label}</span></td>
                <td className="px-4 py-3 text-slate-700">{p.owner_name || "-"}</td>
                <td className="px-4 py-3 font-mono">{p.progress || 0}%</td>
                <td className="px-4 py-3 font-mono">{p.target_date ? formatDate(p.target_date) : "-"}</td>
                <td className="px-4 py-3 font-mono">{p.budget != null ? `$${Number(p.budget).toLocaleString()}` : "-"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProjectDialog({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [risks, setRisks] = useState([]);
  const [form, setForm] = useState(existing ? {
    ...existing,
    target_date: existing.target_date ? existing.target_date.slice(0, 10) : "",
    start_date: existing.start_date ? existing.start_date.slice(0, 10) : "",
    linked_risk_ids: existing.linked_risk_ids || [],
  } : { name: "", description: "", status: "Planning", health: "Green", target_date: "", start_date: "", budget: "", linked_risk_ids: [] });
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/risks?limit=500").then(({ data }) => setRisks(data.items || data || [])).catch(() => {}); }, []);
  const toggleRisk = (id) => setForm({ ...form, linked_risk_ids: form.linked_risk_ids.includes(id) ? form.linked_risk_ids.filter((x) => x !== id) : [...form.linked_risk_ids, id] });
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Project name required");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), description: form.description?.trim() || null, status: form.status, health: form.health,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        target_date: form.target_date ? new Date(form.target_date).toISOString() : null,
        budget: form.budget ? Number(form.budget) : null,
        linked_risk_ids: form.linked_risk_ids || [] };
      if (isEdit) await api.patch(`/projects/${existing.id}`, payload); else await api.post("/projects", payload);
      toast.success(isEdit ? "Updated" : "Project created"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="project-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit project" : "New project"}</DialogTitle>
          <DialogDescription>Track a programme end-to-end - status, health, milestones, budget and linked risks.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. ISO 27001 Stage 1 audit" data-testid="project-name" /></div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Scope, sponsors, success criteria..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="project-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Health</Label><Select value={form.health} onValueChange={(v) => setForm({ ...form, health: v })}><SelectTrigger data-testid="project-health"><SelectValue /></SelectTrigger><SelectContent>{HEALTH.map((h) => <SelectItem key={h.key} value={h.key}>{h.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Target date</Label><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
          </div>
          <div><Label>Budget</Label><Input type="number" step="0.01" value={form.budget || ""} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="0" /></div>
          {risks.length > 0 && (
            <div>
              <Label>Linked risks ({form.linked_risk_ids.length})</Label>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1" data-testid="project-link-risks">
                {risks.slice(0, 50).map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-[12.5px] px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={form.linked_risk_ids.includes(r.id)} onCheckedChange={() => toggleRisk(r.id)} />
                    <span className="font-mono text-[11px] text-slate-500">{r.code}</span>
                    <span className="text-slate-800 truncate">{r.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" disabled={busy} onClick={submit} data-testid="project-save">{busy ? "Saving..." : isEdit ? "Save changes" : "Create project"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDrawer({ projectId, canWrite, onClose, onEdit, onDelete, onChanged }) {
  const [project, setProject] = useState(null);
  const [activity, setActivity] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("board");
  const [commentDraft, setCommentDraft] = useState("");
  const [taskDialog, setTaskDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: a }, { data: c }] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/activity`),
        api.get(`/projects/${projectId}/comments`),
      ]);
      setProject(p); setActivity(a || []); setComments(c || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const updateTask = async (tid, patch) => {
    try { await api.patch(`/projects/${projectId}/tasks/${tid}`, patch); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeTask = async (tid) => {
    try { await api.delete(`/projects/${projectId}/tasks/${tid}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitComment = async () => {
    if (!commentDraft.trim()) return;
    try { await api.post(`/projects/${projectId}/comments`, { body: commentDraft.trim() }); setCommentDraft(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => {
    const g = { Todo: [], InProgress: [], Blocked: [], Done: [] };
    (project?.tasks || []).forEach((t) => { (g[t.status] || g.Todo).push(t); });
    return g;
  }, [project]);

  const isM365 = project?.source?.type === "m365_assessment";
  const sm = project ? statusMeta(project.status) : STATUSES[0];
  const hm = project ? healthMeta(project.health) : HEALTH[0];

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-[820px] p-0 flex flex-col bg-white" data-testid="project-drawer">
        {loading || !project ? <div className="p-6"><LoadingSkeleton /></div> : (
          <>
            {/* Hero banner — solid navy, one subtle cyan accent, no rainbow glow. */}
            <div className="relative overflow-hidden border-b border-slate-200 bg-[#0A1329]">
              <div className="absolute -top-16 -right-20 w-[280px] h-[280px] rounded-full bg-cyan-500/10 blur-2xl pointer-events-none" aria-hidden />

              <div className="relative px-6 pt-5 pb-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/90 bg-white/10 border border-white/20 rounded-full px-2.5 py-1 backdrop-blur-sm`}>
                      <sm.icon className="h-3 w-3" /> {sm.label}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/90 bg-white/10 border border-white/20 rounded-full px-2.5 py-1 backdrop-blur-sm`}>
                      <span className={`h-2 w-2 rounded-full ${hm.color} ring-2 ring-white/30`} /> {hm.label}
                    </span>
                    {isM365 && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-200 bg-cyan-500/15 border border-cyan-400/30 rounded-full px-2.5 py-1 backdrop-blur-sm">
                        <Cloud className="h-3 w-3" /> M365 · CIS {project.source?.control_id}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {canWrite && <Button size="sm" variant="ghost" className="h-8 text-white hover:bg-white/10 hover:text-white" onClick={() => onEdit(project)} data-testid="project-drawer-edit"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
                    {canWrite && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-8 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete project?</AlertDialogTitle>
                            <AlertDialogDescription>"{project.name}" and its tasks, activity and comments will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => onDelete(project.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white transition-colors"><X className="h-4 w-4" /></button>
                  </div>
                </div>

                <div className="flex items-center gap-5">
                  {/* Big progress ring */}
                  <div className="relative h-24 w-24 shrink-0" aria-label={`${project.progress || 0}% complete`}>
                    <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="2.5" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeDasharray={`${project.progress || 0}, 100`} strokeLinecap="round" style={{ filter: "drop-shadow(0 0 6px rgba(34,211,238,0.5))" }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="font-display font-extrabold text-2xl text-white tabular-nums leading-none">{project.progress || 0}%</div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 mt-1">done</div>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display font-bold text-[26px] text-white leading-tight tracking-tight break-words">{project.name}</h2>
                    <div className="mt-2 flex items-center gap-4 text-[12px] text-white/70">
                      <span className="flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" /> {project.owner_name || "Unassigned"}</span>
                      {project.target_date && <span className="flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> {formatDate(project.target_date)}</span>}
                      {project.budget != null && <span className="flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> {Number(project.budget).toLocaleString()}</span>}
                    </div>
                  </div>
                </div>

                {/* Stat chips */}
                <div className="mt-5 grid grid-cols-4 gap-2.5">
                  <StatChip label="Tasks" value={`${project.task_done || 0}/${project.task_total || 0}`} />
                  <StatChip label="Open" value={(project.task_total || 0) - (project.task_done || 0)} />
                  <StatChip label="Milestones" value={(project.tasks || []).filter((t) => t.is_milestone).length} />
                  <StatChip label="Comments" value={comments.length} />
                </div>

                {project.linked_risks?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-[10.5px] font-mono uppercase tracking-widest text-cyan-300 mb-2 flex items-center gap-1"><Link2 className="h-3 w-3" /> Linked risks</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="project-linked-risks">
                      {project.linked_risks.map((r) => (
                        <span key={r.id} className="inline-flex items-center gap-1.5 text-[11px] font-mono border border-white/15 bg-white/10 text-white/90 rounded-full px-2 py-0.5 backdrop-blur-sm">
                          <span className="text-cyan-300">{r.code}</span>
                          <span className="max-w-[160px] truncate">{r.title}</span>
                          <span className={`text-[9px] uppercase font-semibold ${r.current_severity === "Critical" ? "text-rose-300" : r.current_severity === "High" ? "text-orange-300" : r.current_severity === "Moderate" ? "text-amber-300" : "text-emerald-300"}`}>{r.current_severity}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {project.description && (
                <div className="relative px-6 pb-5">
                  <div className="rounded-xl bg-white/95 backdrop-blur-sm border border-white/20 p-4 shadow-xl">
                    <ProjectDescription markdown={project.description} />
                  </div>
                </div>
              )}
            </div>

            <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className={`mx-6 mt-4 mb-2 grid ${isM365 ? "grid-cols-6" : "grid-cols-5"}`}>
                <TabsTrigger value="board" data-testid="tab-board">Board</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                {isM365 && (
                  <TabsTrigger value="cis" data-testid="tab-cis" className="text-cyan-800 data-[state=active]:text-cyan-900">
                    <Cloud className="h-3.5 w-3.5 mr-1" /> CIS Checklist
                  </TabsTrigger>
                )}
                <TabsTrigger value="resources" data-testid="tab-resources">Resources</TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="comments" data-testid="tab-comments">Comments{comments.length > 0 && <span className="ml-1 text-[10px] font-mono opacity-70">{comments.length}</span>}</TabsTrigger>
              </TabsList>

              <TabsContent value="board" className="flex-1 overflow-y-auto px-6 pb-6">
                {canWrite && (
                  <div className="mb-3 flex justify-end">
                    <Button size="sm" onClick={() => setTaskDialog({})} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="project-add-task-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add task</Button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="project-tasks-kanban">
                  {TASK_STATUSES.map((s) => (
                    <div key={s} className="rounded-lg bg-slate-50 border border-slate-200 p-3 min-h-[160px]">
                      <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 mb-2">{s === "InProgress" ? "In progress" : s} · {grouped[s].length}</div>
                      <div className="space-y-2">
                        {grouped[s].map((t) => (
                          <div key={t.id} className="rounded-md bg-white border border-slate-200 p-2.5 text-[12.5px] shadow-sm" data-testid={`project-task-${t.id.slice(0, 8)}`}>
                            <div className="flex items-start gap-1.5">
                              {t.is_milestone && <Flag className="h-3 w-3 text-rose-500 shrink-0 mt-0.5" title="Milestone" />}
                              <div className="font-medium text-slate-900 leading-snug flex-1">{t.title}</div>
                              {t.priority && t.priority !== "Medium" && (
                                <span className={`text-[9px] font-mono uppercase font-semibold px-1 rounded ${t.priority === "High" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{t.priority[0]}</span>
                              )}
                            </div>
                            {(t.assignee_name || t.estimated_hours != null || t.due_date) && (
                              <div className="mt-1 text-[10.5px] font-mono text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5">
                                {t.assignee_name && <span>👤 {t.assignee_name}</span>}
                                {t.estimated_hours != null && (
                                  <span className={t.actual_hours != null && t.actual_hours > t.estimated_hours ? "text-rose-600 font-semibold" : ""}>
                                    ⏱ {t.actual_hours != null ? `${t.actual_hours}/${t.estimated_hours}h` : `${t.estimated_hours}h`}
                                  </span>
                                )}
                                {t.due_date && <span>Due {formatDate(t.due_date)}</span>}
                                {(t.predecessor_ids || []).length > 0 && (
                                  <span title={`${t.predecessor_ids.length} predecessor(s)`}>⇠ {t.predecessor_ids.length}</span>
                                )}
                              </div>
                            )}
                            {canWrite && (
                              <div className="mt-2 flex items-center gap-1">
                                <Select value={t.status} onValueChange={(v) => updateTask(t.id, { status: v })}>
                                  <SelectTrigger className="h-7 text-[11px] w-full"><SelectValue /></SelectTrigger>
                                  <SelectContent>{TASK_STATUSES.map((k) => <SelectItem key={k} value={k}>{k === "InProgress" ? "In progress" : k}</SelectItem>)}</SelectContent>
                                </Select>
                                <button onClick={() => setTaskDialog(t)} className="h-7 px-1.5 rounded text-slate-500 hover:bg-slate-100" title="Edit"><Pencil className="h-3 w-3" /></button>
                                <button onClick={() => removeTask(t.id)} className="h-7 px-1.5 rounded text-rose-500 hover:bg-rose-50"><Trash2 className="h-3 w-3" /></button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="flex-1 overflow-y-auto px-6 pb-6">
                <TaskGantt tasks={project.tasks || []} />
              </TabsContent>

              {isM365 && (
                <TabsContent value="cis" className="flex-1 overflow-y-auto px-6 pb-6">
                  <CisChecklistTab project={project} tasks={project.tasks || []}
                    onTaskToggle={(t) => updateTask(t.id, { status: t.status === "Done" ? "Todo" : "Done" })} />
                </TabsContent>
              )}

              <TabsContent value="resources" className="flex-1 overflow-y-auto px-6 pb-6">
                <ResourceCard resources={project.resources || []} />
              </TabsContent>

              <TabsContent value="activity" className="flex-1 overflow-y-auto px-6 pb-6">
                <ActivityFeed activity={activity} />
              </TabsContent>

              <TabsContent value="comments" className="flex-1 overflow-y-auto px-6 pb-6 flex flex-col">
                <div className="space-y-3 flex-1" data-testid="project-comments">
                  {comments.length === 0 && <div className="text-[13px] text-slate-500 italic">No comments yet. Start the conversation.</div>}
                  {comments.map((c) => {
                    const initials = (c.author_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                    return (
                      <div key={c.id} className="flex gap-3" data-testid={`project-comment-${c.id.slice(0, 8)}`}>
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-[10.5px] font-semibold shrink-0">{initials}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[11.5px] text-slate-600">
                            <span className="font-semibold text-slate-900">{c.author_name}</span>
                            <span className="text-[10px] text-slate-400 font-mono">{formatDateTime(c.created_at)}</span>
                          </div>
                          <div className="mt-1 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">{c.body}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} placeholder="Comment..." className="text-[13px] resize-none" data-testid="project-comment-input" />
                  <Button className="self-end bg-[#0A1329] hover:bg-[#162347] text-white" onClick={submitComment} data-testid="project-comment-send"><SendIcon className="h-3.5 w-3.5" /></Button>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
      {taskDialog !== null && canWrite && project && (
        <TaskDialog task={taskDialog?.id ? taskDialog : null} projectId={projectId} tasks={project.tasks || []}
          onClose={() => setTaskDialog(null)}
          onSaved={() => { setTaskDialog(null); load(); onChanged?.(); }} />
      )}
    </Sheet>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
      <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-[13px] font-semibold text-slate-900 mt-0.5 truncate">{value}</div>
    </div>
  );
}

// ---------------- CIS Checklist tab (M365-sourced projects) ----------------
function CisChecklistTab({ project, tasks, onTaskToggle }) {
  const source = project.source || {};
  const [live, setLive] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const fetchLive = async () => {
    if (!source.control_id) return;
    try {
      const { data } = await api.get(`/m365/control-status/${encodeURIComponent(source.control_id)}`);
      setLive(data);
    } catch (e) {
      // Module might be disabled — surface a friendly note instead of a toast storm.
      setLive({ error: apiError(e) });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLive(); }, [source.control_id]);

  const runRecheck = async () => {
    setBusy(true);
    try {
      await api.post("/m365/scans");
      toast.success("Re-scan complete");
      await fetchLive();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const statusPill = (status) => {
    const map = {
      pass:         { label: "PASS",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
      fail:         { label: "FAIL",         cls: "bg-rose-100 text-rose-800 border-rose-200" },
      manual:       { label: "MANUAL",       cls: "bg-sky-100 text-sky-800 border-sky-200" },
      not_covered:  { label: "NOT COVERED",  cls: "bg-slate-100 text-slate-700 border-slate-200" },
      not_applicable:{ label: "N/A",         cls: "bg-slate-100 text-slate-500 border-slate-200" },
      error:        { label: "ERROR",        cls: "bg-amber-100 text-amber-800 border-amber-200" },
    };
    const m = map[status] || { label: (status || "UNKNOWN").toUpperCase(), cls: "bg-slate-100 text-slate-600 border-slate-200" };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-mono font-semibold border ${m.cls}`}>{m.label}</span>;
  };

  const canWriteTask = typeof onTaskToggle === "function";

  return (
    <div className="space-y-4" data-testid="cis-checklist">
      {/* Header card — live status + recheck */}
      <div className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-800 bg-white/70 border border-cyan-200 rounded px-1.5 py-0.5">
                <Cloud className="h-3 w-3" /> CIS {source.control_id} · {source.level || "-"}
              </span>
              {live && live.has_scan && live.status && statusPill(live.status)}
              {live && live.has_scan === false && (
                <span className="text-[10.5px] font-mono text-slate-500">No scans yet</span>
              )}
              {live && live.not_in_latest && (
                <span className="text-[10.5px] font-mono text-slate-500">Not in latest scan</span>
              )}
              {live && live.error && (
                <span className="text-[10.5px] font-mono text-rose-600">Live status unavailable</span>
              )}
            </div>
            <div className="mt-2 text-[15px] font-display font-semibold text-slate-900 leading-snug">
              {source.control_title || (live && live.title) || "Microsoft 365 CIS control"}
            </div>
            <div className="mt-1 text-[11.5px] font-mono text-slate-500">
              {source.section || live?.section}
              {live?.overall_hardening_pct != null && <span> · tenant hardening {live.overall_hardening_pct}%</span>}
              {live?.scan_at && <span> · last scan {formatDateTime(live.scan_at)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={fetchLive} data-testid="cis-refresh-status" className="h-8">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={runRecheck} disabled={busy}
              className="bg-cyan-700 hover:bg-cyan-800 text-white h-8" data-testid="cis-recheck">
              {busy ? "Rescanning…" : (<><Cloud className="h-3.5 w-3.5 mr-1" /> Re-run scan</>)}
            </Button>
          </div>
        </div>
      </div>

      {/* Expected vs actual */}
      {live && (live.expected || live.actual || live.remediation) && (
        <div className="grid md:grid-cols-2 gap-3">
          {live.expected && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 mb-1">Expected state</div>
              <div className="text-[13px] text-slate-800 leading-relaxed">{live.expected}</div>
            </div>
          )}
          {live.actual && (
            <div className={`rounded-lg border p-3 ${live.status === "pass" ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`}>
              <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${live.status === "pass" ? "text-emerald-800" : "text-rose-800"}`}>What we found</div>
              <div className="text-[13px] text-slate-800 leading-relaxed">{live.actual}</div>
            </div>
          )}
          {live.remediation && (
            <div className="md:col-span-2 rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-1">Remediation guidance</div>
              <div className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{live.remediation}</div>
            </div>
          )}
        </div>
      )}

      {/* Checklist — seeded from the CIS remediation on project creation */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
            Remediation checklist ({tasks.filter((t) => t.status === "Done").length}/{tasks.length} done)
          </div>
          <NavLinkExternal source={source} />
        </div>
        {tasks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-[12px] text-slate-500 italic">
            No sub-tasks were seeded for this control. Add one from the Board tab.
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
            {tasks.map((t) => (
              <label key={t.id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer"
                data-testid={`cis-checklist-item-${t.id.slice(0, 8)}`}>
                <Checkbox
                  checked={t.status === "Done"}
                  disabled={!canWriteTask}
                  onCheckedChange={() => canWriteTask && onTaskToggle(t)}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className={`text-[13px] leading-snug ${t.status === "Done" ? "line-through text-slate-400" : "text-slate-800"}`}>
                    {t.is_milestone && <Flag className="h-3 w-3 text-rose-500 inline mr-1 -mt-0.5" title="Milestone" />}
                    {t.title}
                  </div>
                  {t.description && (
                    <div className="mt-0.5 text-[11.5px] text-slate-500 line-clamp-2">{t.description}</div>
                  )}
                </div>
                {t.due_date && <span className="text-[10.5px] font-mono text-slate-500 shrink-0 mt-1">{formatDate(t.due_date)}</span>}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NavLinkExternal({ source }) {
  // Point users to /assessments so they can drill into the full scan detail.
  return (
    <a
      href="/assessments"
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-widest text-cyan-700 hover:text-cyan-900"
      title="Open full assessment"
      data-testid="cis-open-assessment"
    >
      Open assessment <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function TaskDialog({ task, projectId, tasks, onClose, onSaved }) {
  const isEdit = !!task;
  const [form, setForm] = useState(task ? {
    ...task, due_date: task.due_date ? task.due_date.slice(0, 10) : "", start_date: task.start_date ? task.start_date.slice(0, 10) : "",
    predecessor_ids: task.predecessor_ids || [],
  } : { title: "", description: "", status: "Todo", priority: "Medium", assignee_name: "", estimated_hours: "", actual_hours: "", due_date: "", start_date: "", predecessor_ids: [], is_milestone: false });
  const [busy, setBusy] = useState(false);
  const togglePred = (id) => setForm({ ...form, predecessor_ids: form.predecessor_ids.includes(id) ? form.predecessor_ids.filter((x) => x !== id) : [...form.predecessor_ids, id] });
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { title: form.title.trim(), description: form.description?.trim() || null, status: form.status, priority: form.priority,
        assignee_name: form.assignee_name?.trim() || null,
        estimated_hours: form.estimated_hours !== "" ? Number(form.estimated_hours) : null,
        actual_hours: form.actual_hours !== "" ? Number(form.actual_hours) : null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        predecessor_ids: form.predecessor_ids || [], is_milestone: !!form.is_milestone };
      if (isEdit) await api.patch(`/projects/${projectId}/tasks/${task.id}`, payload);
      else await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success(isEdit ? "Task updated" : "Task created"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const preds = tasks.filter((t) => t.id !== task?.id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription>Assign a resource, estimate hours, set predecessors and mark it a milestone if this is a phase gate.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Draft ISMS scope" data-testid="task-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger><SelectContent>{TASK_STATUSES.map((k) => <SelectItem key={k} value={k}>{k === "InProgress" ? "In progress" : k}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Low","Medium","High"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Assignee</Label><Input value={form.assignee_name || ""} onChange={(e) => setForm({ ...form, assignee_name: e.target.value })} placeholder="Resource name" data-testid="task-assignee" /></div>
            <div className="flex items-center gap-2 pt-6"><Checkbox checked={!!form.is_milestone} onCheckedChange={(v) => setForm({ ...form, is_milestone: !!v })} data-testid="task-milestone" /><Label className="mb-0">Milestone</Label></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" /></div>
            <div><Label>Estimated hours</Label><Input type="number" step="0.5" value={form.estimated_hours ?? ""} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} data-testid="task-est" /></div>
            <div><Label>Actual hours</Label><Input type="number" step="0.5" value={form.actual_hours ?? ""} onChange={(e) => setForm({ ...form, actual_hours: e.target.value })} data-testid="task-actual" /></div>
          </div>
          {preds.length > 0 && (
            <div>
              <Label>Predecessors ({form.predecessor_ids.length})</Label>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1" data-testid="task-predecessors">
                {preds.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-[12px] px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={form.predecessor_ids.includes(p.id)} onCheckedChange={() => togglePred(p.id)} />
                    <span className="text-slate-800 truncate">{p.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" disabled={busy} onClick={submit} data-testid="task-save">{busy ? "Saving..." : isEdit ? "Save changes" : "Create task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskGantt({ tasks }) {
  const dated = tasks.filter((t) => t.start_date && t.due_date);
  if (!dated.length) return <div className="text-[13px] text-slate-500 italic text-center py-10">No tasks with start + due dates. Add both to see the Gantt bars and dependency links.</div>;
  const min = Math.min(...dated.map((t) => new Date(t.start_date).getTime()));
  const max = Math.max(...dated.map((t) => new Date(t.due_date).getTime()));
  const span = Math.max(max - min, 86400000);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="task-gantt">
      {dated.map((t) => {
        const s = new Date(t.start_date).getTime();
        const e = new Date(t.due_date).getTime();
        const left = ((s - min) / span) * 100;
        const width = Math.max(((e - s) / span) * 100, 3);
        const color = t.status === "Done" ? "bg-emerald-500" : t.status === "Blocked" ? "bg-rose-500" : t.status === "InProgress" ? "bg-cyan-500" : "bg-slate-400";
        return (
          <div key={t.id} className="grid grid-cols-[180px_1fr] items-center py-1.5">
            <div className="text-[12.5px] font-medium text-slate-800 truncate flex items-center gap-1.5">
              {t.is_milestone && <Flag className="h-3 w-3 text-rose-500" />} {t.title}
            </div>
            <div className="relative h-6">
              <div className={`absolute top-1/2 -translate-y-1/2 h-4 rounded ${color}`} style={{ left: `${left}%`, width: `${width}%` }} title={`${formatDate(t.start_date)} → ${formatDate(t.due_date)}`} />
              {(t.predecessor_ids || []).length > 0 && <span className="absolute -left-1 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-slate-800" title={`${t.predecessor_ids.length} dependency`} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResourceCard({ resources }) {
  if (!resources.length) return <div className="text-[13px] text-slate-500 italic text-center py-10">No estimates yet. Add assignees and estimated hours to tasks to see resource utilisation.</div>;
  const totalEst = resources.reduce((s, r) => s + (r.estimated_hours || 0), 0);
  const totalAct = resources.reduce((s, r) => s + (r.actual_hours || 0), 0);
  const totalTasks = resources.reduce((s, r) => s + (r.task_count || 0), 0);
  const totalDone = resources.reduce((s, r) => s + (r.done_count || 0), 0);
  const denom = Math.max(totalEst, 1);
  const variancePct = totalEst ? Math.round(((totalAct - totalEst) / totalEst) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="project-resources">
      {/* Aggregate summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Meta label="Resources" value={String(resources.length)} />
        <Meta label="Tasks done" value={`${totalDone}/${totalTasks}`} />
        <Meta label="Planned effort" value={`${totalEst}h`} />
        <Meta label="Actual effort" value={`${totalAct}h${totalEst ? ` (${variancePct >= 0 ? "+" : ""}${variancePct}%)` : ""}`} />
      </div>
      <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 mt-2 mb-1 flex items-center gap-1"><Users2 className="h-3 w-3" /> Per-resource allocation</div>
      {resources.map((r) => {
        const pct = Math.round(((r.estimated_hours || 0) / denom) * 100);
        const actualPct = r.estimated_hours ? Math.round(((r.actual_hours || 0) / r.estimated_hours) * 100) : 0;
        const over = r.actual_hours != null && r.actual_hours > (r.estimated_hours || 0);
        return (
          <div key={r.name} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`resource-${r.name}`}>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="font-medium text-slate-900">{r.name}</span>
              <span className={`font-mono text-[11px] ${over ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                {r.done_count}/{r.task_count} done · {r.estimated_hours}h est · {r.actual_hours}h actual{r.estimated_hours ? ` (${actualPct}%)` : ""}
              </span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden relative">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${pct}%` }} title={`${pct}% of project effort`} />
              {r.actual_hours != null && r.estimated_hours > 0 && (
                <div className={`absolute inset-y-0 left-0 border-r-2 ${over ? "border-rose-600" : "border-slate-800"}`}
                     style={{ width: `${Math.min(pct * Math.min(actualPct, 200) / 100, 100)}%`, mixBlendMode: "multiply" }} title="Actual so far" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityFeed({ activity }) {
  if (!activity.length) return <div className="text-[13px] text-slate-500 italic text-center py-10">No activity yet.</div>;
  return (
    <div className="space-y-3" data-testid="project-activity">
      {activity.map((a) => {
        const initials = (a.actor_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
        const label = actionLabel(a);
        return (
          <div key={a.id} className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-[10.5px] font-semibold shrink-0">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] text-slate-800"><span className="font-semibold">{a.actor_name || "System"}</span> {label}</div>
              <div className="text-[10.5px] font-mono text-slate-400 mt-0.5">{formatDateTime(a.created_at)}</div>
              {a.payload && Object.keys(a.payload).length > 0 && a.payload.snippet && (
                <div className="mt-1 text-[12px] text-slate-600 italic border-l-2 border-slate-200 pl-2">"{a.payload.snippet}"</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function actionLabel(a) {
  if (a.action === "task.created") return `created task "${a.payload?.title || 'a task'}"`;
  if (a.action === "task.deleted") return `deleted task "${a.payload?.title || 'a task'}"`;
  if (a.action === "task.status_changed") return `moved "${a.payload?.title || 'a task'}" → ${a.payload?.status?.to || 'a new state'}`;
  if (a.action === "task.updated") return `updated task "${a.payload?.title || 'a task'}"`;
  if (a.action === "project.updated") return `updated the project`;
  if (a.action === "project.created") return `created the project`;
  if (a.action === "comment.added") return `left a comment`;
  return a.action;
}
